import { useEffect, useRef, useState } from 'react'

/**
 * Tlač v okne priamo v administrácii: náhľad dokladu, tlačidlá Vytlačiť / Nové okno / Zavrieť.
 * Zatvára sa krížikom, klávesom Esc alebo kliknutím mimo okna – bez prepínania kariet prehliadača.
 */
type Job = { url?: string; html?: string; title: string }

export function PrintHost() {
  const [job, setJob] = useState<Job | null>(null)
  const [loading, setLoading] = useState(true)
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState('')
  const frame = useRef<HTMLIFrameElement>(null)

  // Doklad sa načíta cez fetch (s prihlásením) a vloží do okna – funguje aj keď server zakazuje vkladanie stránok.
  useEffect(() => {
    if (!job) return
    if (job.html) { setHtml(job.html); setError(''); return }
    let alive = true
    setHtml(null); setError('')
    fetch(job.url!, { credentials: 'include' })
      .then(async r => {
        const text = await r.text()
        if (r.status === 401) window.dispatchEvent(new Event('emili-admin-session-expired'))
        if (!r.ok) { let msg = ''; try { msg = JSON.parse(text).error } catch { msg = text.slice(0, 200) } throw new Error(msg || `Doklad sa nepodarilo načítať (HTTP ${r.status}).`) }
        // relatívne odkazy (napr. prepínač A4/A5) sa vzťahujú k adrese dokladu
        return text.replace(/<head>/i, `<head><base href="${job.url!.replace(/"/g, '&quot;')}">`)
      })
      .then(text => { if (alive) setHtml(text) })
      .catch(e => { if (alive) { setError(e instanceof Error ? e.message : 'Doklad sa nepodarilo načítať.'); setLoading(false) } })
    return () => { alive = false }
  }, [job])

  useEffect(() => {
    const onPrint = (e: Event) => { const d = (e as CustomEvent<Job>).detail; if (d) { setLoading(true); setJob(d) } }
    window.addEventListener('emili-print', onPrint)
    return () => window.removeEventListener('emili-print', onPrint)
  }, [])
  useEffect(() => {
    if (!job) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setJob(null) }
    window.addEventListener('keydown', onKey)
    document.body.classList.add('ph-lock')
    return () => { window.removeEventListener('keydown', onKey); document.body.classList.remove('ph-lock') }
  }, [job])

  if (!job) return null
  const onLoad = () => {
    setLoading(false)
    try {
      const doc = frame.current?.contentDocument
      // vlastný panel tlačovej stránky skryjeme – tlačidlá sú v hlavičke okna (prepínač A4/A5 ostáva)
      // prepínač A4/A5 a podobné odkazy otvoríme v tomto okne
      doc?.querySelectorAll<HTMLAnchorElement>('.toolbar a[href]').forEach(a => a.addEventListener('click', e => { e.preventDefault(); setLoading(true); setJob(j => j ? { ...j, url: a.href, html: undefined } : j) }))
      const bar = doc?.querySelector('.toolbar') as HTMLElement | null
      if (bar && !bar.querySelector('.fmt')) bar.style.display = 'none'
      else bar?.querySelector('button')?.setAttribute('hidden', '')
      const title = doc?.title
      if (title && job.title === 'Tlač') setJob(j => j ? { ...j, title } : j)
      doc?.addEventListener('keydown', e => { if (e.key === 'Escape') setJob(null) })
    } catch { /* iný pôvod – necháme stránku tak */ }
  }
  const print = () => { try { frame.current?.contentWindow?.focus(); frame.current?.contentWindow?.print() } catch { if (job.url) window.open(job.url, '_blank', 'noopener') } }
  const newWindow = () => {
    const content = html ?? job.html
    const w = window.open('', '_blank')
    if (w && content) { w.document.open(); w.document.write(content); w.document.close() }
    else if (job.url) window.open(job.url, '_blank', 'noopener')
  }

  return <div className="ph-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) setJob(null) }}>
    <section className="ph" role="dialog" aria-modal="true" aria-label={job.title}>
      <header className="ph-head">
        <strong>🖨 {job.title}</strong>
        <div className="ph-actions">
          <button type="button" className="primary-btn" onClick={print} disabled={loading}>Vytlačiť / PDF</button>
          <button type="button" className="pa-ghost" onClick={newWindow} title="Otvoriť v novom okne prehliadača">↗ Nové okno</button>
          <button type="button" className="ph-close" onClick={() => setJob(null)} aria-label="Zavrieť (Esc)" title="Zavrieť (Esc)">×</button>
        </div>
      </header>
      <div className="ph-body">
        {loading && !error && <div className="ph-loading">Pripravujem doklad…</div>}
        {error && <div className="ph-loading ph-error"><p>⚠ {error}</p></div>}
        {html !== null && <iframe ref={frame} title={job.title} srcDoc={html} onLoad={onLoad} />}
      </div>
    </section>
  </div>
}

/** Náhľad tlače vložený priamo do stránky (napr. v detaile objednávky). */
export function PrintPreviewFrame({ url, refreshKey, height = 560 }: { url: string; refreshKey?: unknown; height?: number }) {
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState('')
  const frame = useRef<HTMLIFrameElement>(null)
  useEffect(() => {
    let alive = true
    setError('')
    fetch(url, { credentials: 'include' })
      .then(async r => { const t = await r.text(); if (!r.ok) throw new Error(`Náhľad sa nepodarilo načítať (HTTP ${r.status}).`); return t })
      .then(t => { if (alive) setHtml(t) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Náhľad sa nepodarilo načítať.') })
    return () => { alive = false }
  }, [url, refreshKey])
  if (error) return <p className="error-message">{error}</p>
  if (html === null) return <div className="ph-inline-loading" style={{ height }}>Pripravujem náhľad…</div>
  return <div className="ph-inline" style={{ height }}><iframe ref={frame} title="Náhľad tlače" srcDoc={html} onLoad={() => { try { const bar = frame.current?.contentDocument?.querySelector('.toolbar') as HTMLElement | null; if (bar) bar.style.display = 'none' } catch { /* nevadí */ } }} /></div>
}
